import React, { useState } from 'react';
import { Layout } from '../../components/Layout';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { CallbackModal } from '../../components/CallbackModal';
import { Calendar, Clock, Tag, ChevronLeft, MessageCircle, Plus, Minus, CheckCircle2, MapPin } from 'lucide-react';
import { Link } from 'react-router-dom';
const BOOKING_URL = 'https://denticoclinic.patientportal.onl/bookings';
const WHATSAPP_URL = 'https://wa.me/447345197820';
function AccordionItem({
  question,
  answer,
  isOpen,
  onToggle
}: {
  question: string;
  answer: string;
  isOpen: boolean;
  onToggle: () => void;
}) {
  return <div className="border-b border-gray-100 last:border-b-0" data-id="element-463">
      <button onClick={onToggle} className="w-full flex items-center justify-between py-4 text-left focus:outline-none group" data-id="element-464">
        <span className="text-lg font-medium text-gray-900 group-hover:text-teal-600 transition-colors" data-id="element-465">
          {question}
        </span>
        <span className={`flex-shrink-0 ml-4 w-6 h-6 rounded-full flex items-center justify-center transition-colors ${isOpen ? 'bg-teal-600 text-white' : 'bg-gray-100 text-gray-400'}`} data-id="element-466">
          {isOpen ? <Minus className="w-4 h-4" data-id="element-467" /> : <Plus className="w-4 h-4" data-id="element-468" />}
        </span>
      </button>
      <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-96 opacity-100 pb-4' : 'max-h-0 opacity-0'}`} data-id="element-469">
        <p className="text-gray-600 leading-relaxed" data-id="element-470">{answer}</p>
      </div>
    </div>;
}
export function ChooseDentistArticle() {
  const [isCallbackOpen, setIsCallbackOpen] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);
  const toggleFaq = (index: number) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };
  const faqs = [{
    q: 'Як перевірити, чи лікар легально працює в UK?',
    a: "Кожен стоматолог повинен бути зареєстрований в GDC (General Dental Council). Ви можете перевірити ім'я лікаря на сайті GDC."
  }, {
    q: 'Чи важливі відгуки в Google?',
    a: 'Так, це один з найкращих показників. Звертайте увагу не тільки на оцінку, а й на текст відгуків — про що саме пишуть пацієнти.'
  }, {
    q: 'Чому Angel / Islington — зручний район?',
    a: 'Це центральний Лондон (Zone 1), сюди легко дістатися на метро (Northern Line) або автобусом. Тут багато офісів та кафе, зручно поєднати візит з іншими справами.'
  }, {
    q: 'Чи можна змінити стоматолога, якщо він не сподобався?',
    a: 'Звичайно. Ви не "прив\'язані" до однієї клініки (особливо в приватному секторі). Ви маєте право обирати того, з ким вам комфортно.'
  }, {
    q: 'Що запитати на першій консультації?',
    a: 'Запитайте про план лікування, повну вартість, гарантії та досвід лікаря у вирішенні саме вашої проблеми.'
  }];
  // Schemas
  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: 'Як обрати стоматолога в Angel, Islington? Поради для українців',
    datePublished: '2026-02-12',
    author: {
      '@type': 'Organization',
      name: 'Dentico Clinic'
    },
    publisher: {
      '@type': 'Organization',
      name: 'Dentico Clinic',
      logo: {
        '@type': 'ImageObject',
        url: 'https://denticoclinic.co.uk/logo.png'
      }
    },
    description: 'Поради щодо вибору стоматолога в Лондоні (район Angel). На що звертати увагу: GDC, відгуки, обладнання та мова.',
    inLanguage: 'uk'
  };
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.a
      }
    }))
  };
  return <Layout data-id="element-471">
      <CallbackModal isOpen={isCallbackOpen} onClose={() => setIsCallbackOpen(false)} data-id="element-472" />

      {/* Structured Data */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(articleSchema)
    }} data-id="element-473" />
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(faqSchema)
    }} data-id="element-474" />

      <article className="bg-white" data-id="element-475">
        {/* Header */}
        <div className="bg-teal-50 py-12 lg:py-20" data-id="element-476">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-477">
            <Link to="/ua/blog" className="inline-flex items-center text-teal-600 font-medium mb-6 hover:underline" data-id="element-478">
              <ChevronLeft className="w-4 h-4 mr-1" data-id="element-479" />
              Назад до блогу
            </Link>

            <div className="flex flex-wrap gap-4 mb-6 text-sm text-gray-600" data-id="element-480">
              <span className="bg-white px-3 py-1 rounded-full font-medium text-teal-700 flex items-center shadow-sm" data-id="element-481">
                <Tag className="w-3 h-3 mr-2" data-id="element-482" />
                Поради
              </span>
              <span className="flex items-center" data-id="element-483">
                <Calendar className="w-4 h-4 mr-2" data-id="element-484" />
                Лютий 2026
              </span>
              <span className="flex items-center" data-id="element-485">
                <Clock className="w-4 h-4 mr-2" data-id="element-486" />7 хв читання
              </span>
            </div>

            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6" data-id="element-487">
              Як обрати стоматолога в Angel, Islington? Поради для українців
            </h1>

            <div className="flex items-center" data-id="element-488">
              <div className="w-10 h-10 bg-teal-200 rounded-full flex items-center justify-center text-teal-700 font-bold mr-3" data-id="element-489">
                DC
              </div>
              <div data-id="element-490">
                <p className="font-medium text-gray-900" data-id="element-491">Dentico Clinic Team</p>
                <p className="text-sm text-gray-500" data-id="element-492">Експертна стаття</p>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16" data-id="element-493">
          <div className="prose prose-lg prose-teal max-w-none text-gray-600" data-id="element-494">
            <p className="lead text-xl text-gray-800 font-medium mb-8" data-id="element-495">
              Знайти "свого" лікаря важко навіть вдома, а в новому місті, та ще
              й в іншій країні — це справжній виклик. Лондон величезний, і
              клінік тут тисячі. Як обрати ту, де вам нададуть якісну допомогу,
              не нашкодять і не візьмуть зайвих грошей?
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-6" data-id="element-496">
              Чому Angel / Islington?
            </h2>
            <p className="mb-6" data-id="element-497">
              Район Angel (Islington) — це один з найзручніших районів
              центрального Лондона.
            </p>
            <ul className="space-y-2 mb-8" data-id="element-498">
              <li className="flex items-start" data-id="element-499">
                <MapPin className="w-5 h-5 text-teal-600 mr-2 mt-1 flex-shrink-0" data-id="element-500" />
                <span data-id="element-501">
                  <strong data-id="element-502">Транспорт:</strong> Станція Angel (Northern Line)
                  дозволяє швидко дістатися з King's Cross, Bank або London
                  Bridge.
                </span>
              </li>
              <li className="flex items-start" data-id="element-503">
                <MapPin className="w-5 h-5 text-teal-600 mr-2 mt-1 flex-shrink-0" data-id="element-504" />
                <span data-id="element-505">
                  <strong data-id="element-506">Інфраструктура:</strong> Тут багато офісів, магазинів
                  та кафе, тому візит до лікаря легко вписати в графік.
                </span>
              </li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-6" data-id="element-507">
              На що звертати увагу при виборі?
            </h2>

            <h3 className="text-xl font-bold text-gray-900 mt-6 mb-4" data-id="element-508">
              1. Реєстрація в GDC
            </h3>
            <p className="mb-6" data-id="element-509">
              У Великобританії кожен стоматолог, гігієніст та медсестра повинні
              бути зареєстровані в <strong data-id="element-510">General Dental Council (GDC)</strong>
              . Це гарантія того, що лікар має відповідну освіту і право на
              практику. Ви можете перевірити будь-якого лікаря на сайті GDC за
              прізвищем.
            </p>

            <h3 className="text-xl font-bold text-gray-900 mt-6 mb-4" data-id="element-511">
              2. Відгуки пацієнтів
            </h3>
            <p className="mb-6" data-id="element-512">
              Google Reviews — це найчесніший показник. Шукайте клініки з
              рейтингом 4.8-5.0. Читайте, що саме пишуть люди: чи було боляче?
              Чи були вони задоволені результатом? Чи привітний персонал?
            </p>

            <h3 className="text-xl font-bold text-gray-900 mt-6 mb-4" data-id="element-513">
              3. Обладнання
            </h3>
            <p className="mb-6" data-id="element-514">
              Сучасна стоматологія — це цифрові технології. Хороша клініка
              повинна мати:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-8" data-id="element-515">
              <li data-id="element-516">Цифровий рентген (менше опромінення).</li>
              <li data-id="element-517">Інтраоральні камери (щоб показати вам стан зуба).</li>
              <li data-id="element-518">Мікроскоп (для лікування каналів).</li>
            </ul>

            <h3 className="text-xl font-bold text-gray-900 mt-6 mb-4" data-id="element-519">
              4. Мова спілкування
            </h3>
            <p className="mb-6" data-id="element-520">
              Медична термінологія англійською може бути складною. Можливість
              пояснити свої симптоми і зрозуміти лікаря рідною мовою — це
              величезний плюс, який знімає стрес.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-6" data-id="element-521">
              Чому Dentico Clinic?
            </h2>
            <p className="mb-6" data-id="element-522">
              Ми пишаємося тим, що створили в Angel клініку, яка відповідає
              найвищим стандартам:
            </p>
            <ul className="space-y-3 mb-8" data-id="element-523">
              <li className="flex items-start" data-id="element-524">
                <CheckCircle2 className="w-5 h-5 text-teal-600 mr-3 mt-1 flex-shrink-0" data-id="element-525" />
                <span data-id="element-526">
                  <strong data-id="element-527">Досвід:</strong> Наші лікарі мають багаторічний досвід
                  роботи в UK.
                </span>
              </li>
              <li className="flex items-start" data-id="element-528">
                <CheckCircle2 className="w-5 h-5 text-teal-600 mr-3 mt-1 flex-shrink-0" data-id="element-529" />
                <span data-id="element-530">
                  <strong data-id="element-531">Репутація:</strong> Ми маємо відмінні відгуки від
                  пацієнтів.
                </span>
              </li>
              <li className="flex items-start" data-id="element-532">
                <CheckCircle2 className="w-5 h-5 text-teal-600 mr-3 mt-1 flex-shrink-0" data-id="element-533" />
                <span data-id="element-534">
                  <strong data-id="element-535">Комфорт:</strong> Ми говоримо українською, російською
                  та англійською.
                </span>
              </li>
            </ul>
          </div>

          {/* FAQ Section */}
          <div className="mt-16 pt-10 border-t border-gray-100" data-id="element-536">
            <h3 className="text-2xl font-bold text-gray-900 mb-8" data-id="element-537">
              Часті запитання
            </h3>
            <div className="space-y-2" data-id="element-538">
              {faqs.map((faq, idx) => <AccordionItem key={idx} question={faq.q} answer={faq.a} isOpen={openFaqIndex === idx} onToggle={() => toggleFaq(idx)} data-id="element-539" />)}
            </div>
          </div>

          {/* Read Also */}
          <div className="mt-16" data-id="element-540">
            <h3 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-541">
              Читайте також
            </h3>
            <div className="grid sm:grid-cols-2 gap-4" data-id="element-542">
              <Link to="/ua/ukrainskyi-stomatolog-london" data-id="element-543">
                <Card className="p-4 hover:shadow-md transition-shadow h-full flex items-center" data-id="element-544">
                  <span className="text-teal-600 font-medium" data-id="element-545">
                    Про Dentico Clinic
                  </span>
                </Card>
              </Link>
              <Link to="/ua/contact" data-id="element-546">
                <Card className="p-4 hover:shadow-md transition-shadow h-full flex items-center" data-id="element-547">
                  <span className="text-teal-600 font-medium" data-id="element-548">
                    Наші контакти
                  </span>
                </Card>
              </Link>
            </div>
          </div>
        </div>

        {/* CTA */}
        <section className="py-16 bg-teal-900" data-id="element-549">
          <div className="max-w-4xl mx-auto px-4 text-center" data-id="element-550">
            <h2 className="text-3xl font-bold text-white mb-4" data-id="element-551">
              Завітайте до нас в Angel
            </h2>
            <p className="text-xl text-teal-100 mb-8 max-w-2xl mx-auto" data-id="element-552">
              Ми знаходимось всього в 5 хвилинах від метро.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-553">
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" data-id="element-554">
                <Button size="lg" className="w-full gap-2 bg-white text-teal-900 hover:bg-teal-50" data-id="element-555">
                  <Calendar className="w-5 h-5" data-id="element-556" />
                  Записатись онлайн
                </Button>
              </a>
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" data-id="element-557">
                <Button variant="outline" size="lg" className="w-full gap-2 border-teal-400 text-teal-100 hover:bg-teal-800 hover:text-white" data-id="element-558">
                  <MessageCircle className="w-5 h-5" data-id="element-559" />
                  WhatsApp
                </Button>
              </a>
            </div>
          </div>
        </section>
      </article>
    </Layout>;
}